"use client"

import { useEffect, useState } from "react"
import type { BooruPost } from "@/lib/booru-api"
import { NavigationBar } from "./navigation-bar"
import { BooruGrid } from "./booru-grid"
import { Loader2, AlertCircle } from "@/lib/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useSourceStore } from "@/lib/source-store"
import { useTranslations } from "next-intl"

interface BoardGridViewProps {
  boardId: string
  boardName: string
  onBack: () => void
  onPostClick?: (post: BooruPost) => void
}

export function BoardGridView({ boardId, boardName, onBack, onPostClick }: BoardGridViewProps) {
  const t = useTranslations("boardGridView")
  const [boardPosts, setBoardPosts] = useState<BooruPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Get source store for 4chan thumbnail quality setting
  const { getSource } = useSourceStore()

  useEffect(() => {
    // Load board threads
    const loadBoardThreads = async () => {
      try {
        setLoading(true)
        setError(null)

        // Use our API route to avoid CORS issues
        const apiUrl = `/api/4chan/board?board=${boardId}`
        console.log(`[v0] Fetching board /${boardId}/ via API: ${apiUrl}`)

        const response = await fetch(apiUrl)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `Failed to fetch board: ${response.statusText}`)
        }

        const data = await response.json()
        
        // Get thumbnail quality setting from source config
        const source = getSource("4chan")
        const useLowQuality = source?.thumbnailQuality !== "full"
        
        const posts: BooruPost[] = []

        // Parse catalog data
        for (const page of data) {
          if (!page.threads) continue

          for (const thread of page.threads) {
            // Filter out gore/death content
            const comment = (thread.com || "").toLowerCase()
            const subject = (thread.sub || "").toLowerCase()
            const blacklist = ["gore", "death", "rekt", "dead", "corpse", "murder", "suicide", "blood", "injury", "accident", "fatal"]
            const isBlacklisted = blacklist.some(tag =>
              comment.includes(tag.toLowerCase()) || subject.includes(tag.toLowerCase())
            )

            if (isBlacklisted) continue

            // Only include threads with images
            if (!thread.tim || !thread.ext) continue

            // Construct 4chan image URLs
            const ext = thread.ext.toLowerCase()
            const isVideo = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv|gif|gifv)$/i.test(ext)
            const fullUrl = `https://i.4cdn.org/${boardId}/${thread.tim}${thread.ext}`
            const thumbUrl = `https://i.4cdn.org/${boardId}/${thread.tim}s.jpg`

            posts.push({
              id: String(thread.no),
              fileUrl: fullUrl,
              // Use thumbnail or full based on user setting
              previewUrl: useLowQuality ? thumbUrl : fullUrl,
              sampleUrl: fullUrl,
              tags: [`board:${boardId}`, ...(subject ? [subject] : [])],
              rating: "explicit",
              score: thread.replies || 0,
              source: "4chan",
              site: "4chan",
              type: "album",
              albumMediaCount: (thread.images || 0) + 1,
              width: thread.w,
              height: thread.h,
              postUrl: `https://boards.4chan.org/${boardId}/thread/${thread.no}`,
            })
          }
        }

        console.log(`[v0] Found ${posts.length} threads in /${boardId}/`)
        setBoardPosts(posts)
      } catch (err) {
        console.error("[v0] Failed to load board threads:", err)
        setError(err instanceof Error ? err.message : "Failed to load board threads")
      } finally {
        setLoading(false)
      }
    }

    loadBoardThreads()
  }, [boardId])

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <NavigationBar
          sourceName="4chan"
          albumTitle={boardName}
          onBack={onBack}
        />
        <div className="flex items-center justify-center flex-1 p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <NavigationBar
          sourceName="4chan"
          albumTitle={boardName}
          onBack={onBack}
        />
        <div className="flex items-center justify-center flex-1 p-8">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("failedToLoadBoard")}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>{error}</p>
              <div className="flex gap-2">
                <Button onClick={onBack} variant="outline" size="sm">
                  {t("goBack")}
                </Button>
                <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                  {t("retry")}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  if (boardPosts.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <NavigationBar
          sourceName="4chan"
          albumTitle={boardName}
          onBack={onBack}
        />
        <div className="flex items-center justify-center flex-1 p-8">
          <Alert className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("noThreadsFound")}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>{t("thisBoardHasNoThreadsAtTheMoment")}</p>
              <Button onClick={onBack} variant="outline" size="sm" className="w-fit">
                {t("goBack")}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <NavigationBar
        sourceName="4chan"
        albumTitle={boardName}
        onBack={onBack}
      />
      <div className="flex-1 overflow-y-auto">
        <BooruGrid
          posts={boardPosts}
          onPostClick={onPostClick}
          isAlbumView={false}
        />
      </div>
    </div>
  )
}
